import { useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useFormContext, useFormState } from 'react-hook-form';

import { DescriptionButton } from 'common/components/DescriptionButton';
import { DescriptionBox } from 'common/components/DescriptionBox';
import { TOfferEventFormValues } from 'offer/types';
import { MultipleDateRangeFields } from 'common/forms/Field/MultipleDateRangeFields';
import { LocationSelectField, SwitchField } from 'common/forms/Field';
import { WebinarLinkField } from 'offer/forms/OfferEventForm/WebinarLinkField';

interface IProps {
    hasServiceProvider?: boolean;
    offerEventId?: string;
}

export const MeetingFormFields = ({
    hasServiceProvider,
    offerEventId,
}: IProps) => {
    const [webinarDescriptionOpen, setWebinarDescriptionOpen] = useState(false);
    const [translate] = useTranslation();

    const { control } = useFormContext<TOfferEventFormValues>();
    const { errors } = useFormState<TOfferEventFormValues>();

    return (
        <MultipleDateRangeFields
            control={control}
            extraFields={(dateFieldName, index, { startDate, endDate }) => {
                const dateErrors = errors?.dates?.[index];

                const hasError = !!(
                    dateErrors?.startDate?.message ||
                    dateErrors?.endDate?.message
                );

                const availabilityFilters =
                    hasServiceProvider && !hasError && startDate && endDate
                        ? {
                              availabilityStartDate: startDate.toISO(),
                              availabilityEndDate: endDate.toISO(),
                          }
                        : undefined;

                return (
                    <>
                        <LocationSelectField
                            allowCreateString
                            allowRefetch={!hasError}
                            availabilityFilters={availabilityFilters}
                            label={translate('location')}
                            name={`${dateFieldName}.location`}
                        />

                        {!!hasServiceProvider && (
                            <>
                                <Grid
                                    container
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Grid sx={{ mr: -1 }}>
                                        <SwitchField
                                            control={control}
                                            label={translate(
                                                'offer.teamsMeeting'
                                            )}
                                            name={`${dateFieldName}.isServiceProviderWebinar`}
                                        />
                                    </Grid>
                                    <Grid>
                                        <DescriptionButton
                                            onClick={() =>
                                                setWebinarDescriptionOpen(
                                                    !webinarDescriptionOpen
                                                )
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <DescriptionBox
                                    open={webinarDescriptionOpen}
                                    onClose={() =>
                                        setWebinarDescriptionOpen(false)
                                    }
                                >
                                    {translate('offer.teamsMeetingDescription')}
                                </DescriptionBox>
                            </>
                        )}

                        <WebinarLinkField
                            dateFieldName={dateFieldName}
                            hasServiceProvider={hasServiceProvider}
                        />
                    </>
                );
            }}
            extraFieldsInitialValues={{
                location: null,
                webinarLink: '',
                isServiceProviderWebinar: hasServiceProvider,
            }}
            name="dates"
            offerEventId={offerEventId}
        />
    );
};
